/**
 * Network feed drop handler (PRD-05b).
 *
 * Listens for a dropped UK BODS / TransXChange feed (`networkFeedDropped`, emitted by the
 * map drop handler) — including a re-dropped Atlas export — and ingests it into the
 * **network store** as a hosted feed: upload → poll ingest status → select it on the map.
 * A full network belongs in the store (browsable/forkable), not flattened into the Fixed
 * Routes drawer, so this is deliberately separate from the GTFS-zip → drawer import.
 *
 * Surfaces live status the whole way (uploading → processing step/reason → imported with
 * counts, or failed with the reason) via a Snackbar. Mounted once near the map.
 */

import React, { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Alert, CircularProgress, Snackbar } from '@mui/material';

import { useNetworkBrowse } from '../../../context/NetworkBrowseContext';
import {
  getFeedIngestStatus,
  uploadFeed,
  type FeedIngestStatus,
} from '../../../services/network/networkApi';
import { NETWORK_FEED_DROPPED_EVENT } from '../handlers/gtfsImportEvent';

const POLL_INTERVAL_MS = 1500;
const MAX_POLLS = 600; // ~15 min — a national feed's ingest is multi-minute

const delay = (ms: number): Promise<void> => new Promise((r) => setTimeout(r, ms));

const slugify = (name: string): string =>
  name.replace(/\.[^.]+$/, '').toLowerCase().replace(/[^a-z0-9]+/g, '-').replace(/^-+|-+$/g, '').slice(0, 48)
  || 'feed';

interface Snack {
  severity: 'info' | 'success' | 'error';
  message: string;
  busy: boolean; // persist + show a spinner while the import is in flight
}

const NetworkFeedDropHandler: React.FC = () => {
  const { t } = useTranslation(['mapCore']);
  const { selectFeed, setPickerOpen } = useNetworkBrowse();
  const [snack, setSnack] = useState<Snack | null>(null);
  const busyRef = useRef(false);

  useEffect(() => {
    const handle = async (evt: Event) => {
      const file = (evt as CustomEvent<{ file: File }>).detail?.file;
      if (!file || busyRef.current) return;
      busyRef.current = true;

      // Human-readable step text: prefer the backend's `reason`, fall back to `step`.
      const describe = (s: FeedIngestStatus): string => s.reason || s.step || '';
      const processing = (s: FeedIngestStatus) =>
        setSnack({
          severity: 'info', busy: true,
          message: describe(s)
            ? t('networkDrop.processing', { name: file.name, detail: describe(s) })
            : t('networkDrop.ingesting', { name: file.name }),
        });

      setSnack({ severity: 'info', busy: true, message: t('networkDrop.uploading', { name: file.name }) });
      try {
        const { feedId } = await uploadFeed(file, slugify(file.name));
        let status = await getFeedIngestStatus(feedId);
        processing(status);
        for (let i = 0; i < MAX_POLLS && status.state === 'ingesting'; i += 1) {
          await delay(POLL_INTERVAL_MS);
          status = await getFeedIngestStatus(feedId);
          processing(status);
        }
        if (status.state !== 'ready') {
          throw new Error(status.error || status.reason || t('networkDrop.timeout'));
        }
        selectFeed(feedId);
        setPickerOpen(true);
        const c = status.counts;
        setSnack({
          severity: 'success', busy: false,
          message: c?.routes != null && c?.stops != null
            ? t('networkDrop.readyCounts', { routes: c.routes, stops: c.stops })
            : t('networkDrop.ready'),
        });
      } catch (err) {
        const raw = (err as { message?: string }).message || '';
        const message = /authentication token/i.test(raw)
          ? t('networkDrop.signIn')
          : t('networkDrop.failed', { error: raw || t('networkDrop.unknownError') });
        setSnack({ severity: 'error', busy: false, message });
      } finally {
        busyRef.current = false;
      }
    };
    window.addEventListener(NETWORK_FEED_DROPPED_EVENT, handle);
    return () => window.removeEventListener(NETWORK_FEED_DROPPED_EVENT, handle);
  }, [t, selectFeed, setPickerOpen]);

  return (
    <Snackbar
      open={!!snack}
      autoHideDuration={snack && !snack.busy ? 8000 : null}
      onClose={(_e, reason) => {
        if (reason === 'clickaway') return;
        setSnack(null);
      }}
      anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
    >
      {snack ? (
        <Alert
          severity={snack.severity}
          variant="filled"
          icon={snack.busy ? <CircularProgress size={18} color="inherit" /> : undefined}
          onClose={snack.busy ? undefined : () => setSnack(null)}
          sx={{ maxWidth: 520, alignItems: 'center' }}
        >
          {snack.message}
        </Alert>
      ) : undefined}
    </Snackbar>
  );
};

export default NetworkFeedDropHandler;
